import { useState, useRef, useEffect } from 'react';
import { translate } from 'react-i18next';
import moment from 'moment';

// Services && Constants
import { bookingApi } from 'services/http';
import { dateWithTimezone } from 'src/services/date';
import { dateForRequest } from 'services/date';
import { getAppointmentViewState } from 'src/services/functions';
import { STATUS_BOOKED } from 'src/services/constants';
import { sortAppointmentsForDateTime } from 'src/services/date';

// Components
import ArrivalDelay from 'src/views/practitioner/components/ArrivalDelay';
import PatientBlock from 'src/views/practitioner/Appointments/components/Table/PatientBlock';
import PractitionersList from '../../../../FiltersSection/PractitionersList';
import PreLoader from 'src/components/PreLoader';

// Styles & Images
import BlueCheckMark from 'assets/images/check-mark-blue.svg';
import EmptyBookSlots from 'assets/images/empty-book-slots.svg';
import { Time } from 'src/views/practitioner/Appointments/components/Table/tableStyles';
import { AppointmentCard } from '../styles';
import { ModalText, ModalBlockSection } from 'components/CustomModal/styles';

const R_AppointmentsList = ({
  i18n,
  prefPractitionerId,
  practitionersList,
  onSelectExistAppointment
}) => {
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [selectAppointment, setSelectAppointment] = useState(null);
  const [practitionerId, setPractitionerId] = useState(prefPractitionerId);
  const [isScrollActive, setScrollOption] = useState(false);
  const [showPreLoader, triggerPreloader] = useState(false);

  const scrollEl = useRef(null);

  useEffect(
    () => {
      if (
        scrollEl &&
        scrollEl.current &&
        scrollEl.current.scrollHeight > scrollEl.current.clientHeight
      ) {
        setScrollOption(true);
      }
    },
    [appointmentsList]
  );

  const returnUpdates = data => {
    if (onSelectExistAppointment) {
      onSelectExistAppointment(data);
    }
  };

  const setAppointment = ap => {
    let selectAp;
    if (
      selectAppointment &&
      selectAppointment.id &&
      ap &&
      ap.id &&
      selectAppointment.id === ap.id
    ) {
      selectAp = null;
    } else {
      selectAp = ap;
    }
    setSelectAppointment(selectAp);
    returnUpdates(selectAp);
  };

  const onChangePractitioner = doctor => {
    if (doctor && doctor.id) {
      setPractitionerId(doctor.id);
      setSelectAppointment(null);
      returnUpdates(null);
    }
  };

  useEffect(
    () => {
      if (practitionerId) {
        triggerPreloader(true);
        bookingApi
          .get(`practitioners/${practitionerId}/bookings`, {
            params: {
              startDateTime: dateForRequest(moment().startOf('day')),
              endDateTime: dateForRequest(moment().endOf('day'))
            },
            withoutLoader: true
          })
          .then(({ data }) => {
            triggerPreloader(false);
            if (data && Array.isArray(data)) {
              const onlyNewPatients = data.filter(el => {
                const apStatus = getAppointmentViewState(el);
                return apStatus === STATUS_BOOKED;
              });
              const sortForTime = sortAppointmentsForDateTime(onlyNewPatients);
              setAppointmentsList(sortForTime);
            }
          })
          .catch(() => {
            triggerPreloader(false);
          });
      }
    },
    [practitionerId]
  );

  const renderCardList = () =>
    appointmentsList && Array.isArray(appointmentsList) && appointmentsList.length ? (
      appointmentsList.map(ap => (
        <AppointmentCard
          key={ap.id}
          isActive={selectAppointment && ap.id === selectAppointment.id}
          onClick={() => {
            setAppointment(ap);
          }}
        >
          <Time className="time-block">
            <div>
              <span>{dateWithTimezone(ap && ap.dateTime)}</span>
              <ArrivalDelay appointment={ap} />
            </div>
          </Time>

          <div className="patient-block">
            <PatientBlock patient={ap.patient} />
          </div>

          <img src={BlueCheckMark} alt="blue check mark" />
        </AppointmentCard>
      ))
    ) : (
      <div className="empty-data">
        <img src={EmptyBookSlots} alt="don't have temp users" />
        <p>{i18n.t('no_data')}</p>
      </div>
    );

  return (
    <section className="appointments-list">
      <PreLoader show={showPreLoader} isLocal={true} />
      <h3>{i18n.t('temporary_patients')}</h3>
      <ModalBlockSection>
        <ModalText className="bn-m-small">{i18n.t('select_practitioner')}</ModalText>
        <PractitionersList
          practitioners={practitionersList}
          practitionerId={practitionerId}
          onChangePractitioner={onChangePractitioner}
        />
      </ModalBlockSection>
      <div className={`scroll-content ${isScrollActive ? 'with-scroll' : ''}`} ref={scrollEl}>
        {renderCardList()}
      </div>
    </section>
  );
};

export default translate()(R_AppointmentsList);
